import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderV2 } from '@kotipizzagroup/kotipizza-orders-api-client';
import ScheduleIcon from '@mui/icons-material/Schedule';
import styled from 'styled-components';
import { OrderStatusChip } from '../OrderStatusChip';
import { PanelTopBar } from './Styled';
import { formatOrderMoment, getDeliveryTypeLabel } from '../utils/orderRowFields';
import { orderStatusFromTimeStamps } from '../utils/orderStatus';
import { CopyToClipboardButton } from '../../CopyToClipboard/CopyToClipboardButton';

type Props = {
  order: OrderV2;
  /**
   * Rendered at the end of the title row — the panel passes its close button so the way out shares the
   * row with the title instead of costing the panel a band of its own. The full page passes nothing.
   */
  action?: React.ReactNode;
  /** Pins the title row while the rest scrolls under it. On for the panel, off for the full page. */
  sticky?: boolean;
};

/**
 * What the order is, and where it has got to.
 *
 * The title names the kind of order and its number — the two things you would say out loud to identify
 * it — and shares its row with nothing but the close button, because that row is what stays pinned in
 * the panel. The status, as the same chip the list uses so an order can never be described differently
 * here than in the row that opened it, sits just below with the preorder badge, and scrolls away.
 *
 * Everything else the header used to carry has moved: the timestamps into the details box, the receipt
 * download into its own Toiminnot card.
 *
 * Rendered as siblings rather than wrapped in a header element of its own: the pinned row has to be a
 * direct child of `OrderDetailsView`'s layout column to stay pinned for the whole of the scroll.
 */
export const OrderDetailsHeader: React.FC<Props> = ({ order, action, sticky = false }) => {
  const { t } = useTranslation();
  const [sentinel, isStuck] = useStuck(sticky);

  return (
    <>
      {sticky && <StuckSentinel ref={sentinel} />}

      <PanelTopBar $sticky={sticky} $raised={isStuck}>
        <Title>
          {`${getDeliveryTypeLabel(order, t)} #${order.id}`}
          {/* Beside the number rather than out at the row's end, so it reads as belonging to the id
              and not to the panel. Inside the heading, which keeps the two together when a long
              delivery type wraps the title onto a second line. */}
          {order.id ? <CopyOrderIdButton value={order.id} label={t('order.copyId')} /> : null}
        </Title>
        {action}
      </PanelTopBar>

      <Meta>
        <OrderStatusChip status={orderStatusFromTimeStamps(order)} />

        {order.preOrderTime && (
          <PreorderBadge>
            <ScheduleIcon fontSize="small" />
            <strong>{t('Preorder')}</strong>
            <span>{formatOrderMoment(order.preOrderTime, t)}</span>
          </PreorderBadge>
        )}
      </Meta>
    </>
  );
};

/**
 * Whether the pinned row has left its place in the flow and is covering content — which is what earns it
 * its shadow, and what makes a shadow wrong before it happens.
 *
 * A sliver of an element at the very top of the content answers that on its own: once it is scrolled out
 * of the panel, the row is pinned. An observer rather than a scroll listener, because it fires only at
 * the crossing and needs no reference to whichever ancestor happens to be the one scrolling — the panel's
 * scroller clips the sentinel away, and that is enough for the observer to see it go.
 */
const useStuck = (enabled: boolean) => {
  const sentinel = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const node = sentinel.current;

    if (!enabled || !node) {
      setIsStuck(false);
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => setIsStuck(!entry.isIntersecting));
    observer.observe(node);

    return () => observer.disconnect();
  }, [enabled]);

  return [sentinel, isStuck] as const;
};

// Out of flow, so it costs the column neither height nor one of its gaps, and behind the pinned row,
// which is harmless: the observer measures geometry and does not care what covers it.
const StuckSentinel = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 1px;
`;

// `flex: 1` rather than the row's `justify-content`: it is what holds the close button at the right edge
// however short the title is. `anywhere` so a long delivery type wraps instead of pushing the button off.
const Title = styled.h2`
  flex: 1;
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
`;

/**
 * Sized against the heading rather than left at the icon's own 24px, which beside 24px text read as a
 * second title. `vertical-align` because it sits in the heading's text flow, where the default
 * baseline alignment would hang it below the digits it belongs to.
 */
const CopyOrderIdButton = styled(CopyToClipboardButton)`
  && {
    margin-left: 8px;
    vertical-align: middle;

    svg {
      font-size: 18px;
    }
  }
`;

// The status and, where there is one, the preorder time: one row where they fit, wrapping where they do
// not, which on a phone they generally do not.
const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

// Sized to its content rather than spanning the column — a badge, not a banner. The yellow is the
// one the list marks preorder rows with.
const PreorderBadge = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  padding: 4px 12px;
  border-radius: 999px;
  background: ${({ theme }) => theme.palette.highlight.main};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;
